import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowLeft, 
  Eye, 
  Droplets, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Hand, 
  RefreshCw,
  Shield,
  Users,
  Calendar,
  Timer
} from 'lucide-react';
import './App.css';

const LensInstructions = () => {
  const [activeTab, setActiveTab] = useState('daily');

  const Logo = () => (
    <Link to="/" className="logo-container">
      <img 
        src={require('./NOVA logo icon.png')} 
        alt="Nova Logo" 
        className="logo-image"
        style={{ height: '2.5rem', width: 'auto' }}
      />
      <span className="logo-text">Nova</span>
    </Link>
  );

  const instructionSteps = {
    daily: [
      {
        step: 1,
        title: "Wash Your Hands",
        description: "Always wash your hands thoroughly with soap and dry them with a lint-free towel before handling lenses.",
        icon: <Hand size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 2,
        title: "Remove from Package",
        description: "Gently remove the lens from its sterile packaging. Check that it's not inside-out (it should look like a bowl).",
        icon: <Eye size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 3,
        title: "Rinse if Needed",
        description: "If the lens appears to have debris, rinse with contact solution (never use tap water).",
        icon: <Droplets size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 4,
        title: "Insert the Lens",
        description: "Place on your fingertip, hold your eyelid open, and gently place the lens on your eye.",
        icon: <CheckCircle size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 5,
        title: "Blink and Check",
        description: "Blink a few times to center the lens. It should feel comfortable and provide clear vision.",
        icon: <Eye size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 6,
        title: "Dispose After Use",
        description: "Remove and dispose of daily lenses at the end of the day. Never reuse daily disposable lenses.",
        icon: <RefreshCw size={32} style={{ color: '#0d9488' }} />
      }
    ],
    weekly: [
      {
        step: 1,
        title: "Clean Hands",
        description: "Wash hands thoroughly with soap and dry with a clean, lint-free towel.",
        icon: <Hand size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 2,
        title: "Remove Lens",
        description: "Gently remove the lens and place it in your clean palm.",
        icon: <Eye size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 3,
        title: "Clean with Solution",
        description: "Apply a few drops of contact solution and gently rub the lens for 20 seconds.",
        icon: <Droplets size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 4,
        title: "Rinse Thoroughly",
        description: "Rinse both sides of the lens with fresh contact solution.",
        icon: <RefreshCw size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 5,
        title: "Store Properly",
        description: "Place lens in clean case filled with fresh solution. Never reuse old solution.",
        icon: <Shield size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 6,
        title: "Replace Weekly",
        description: "Discard lenses after 7 days of use, regardless of how often they were worn.",
        icon: <Calendar size={32} style={{ color: '#0d9488' }} />
      }
    ],
    monthly: [
      {
        step: 1,
        title: "Hand Hygiene",
        description: "Always start with clean hands washed with soap and dried with a lint-free towel.",
        icon: <Hand size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 2,
        title: "Gentle Removal",
        description: "Remove lens carefully and place in clean palm for cleaning.",
        icon: <Eye size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 3,
        title: "Daily Cleaning",
        description: "Clean with multi-purpose solution using a gentle rubbing motion for 20-30 seconds.",
        icon: <Droplets size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 4,
        title: "Thorough Rinse",
        description: "Rinse lens completely with fresh solution to remove all cleaning residue.",
        icon: <RefreshCw size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 5,
        title: "Fresh Storage",
        description: "Store in clean case with fresh solution every night. Replace case every 3 months.",
        icon: <Shield size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 6,
        title: "Monthly Replacement",
        description: "Replace lenses after 30 days of use. Mark your calendar to track replacement dates.",
        icon: <Calendar size={32} style={{ color: '#0d9488' }} />
      }
    ],
    yearly: [
      {
        step: 1,
        title: "Meticulous Hand Hygiene",
        description: "Always wash hands thoroughly with antibacterial soap and dry with a fresh, lint-free towel before any lens handling.",
        icon: <Hand size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 2,
        title: "Careful Lens Removal",
        description: "Remove lens gently using proper technique. Place immediately in palm for thorough cleaning.",
        icon: <Eye size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 3,
        title: "Deep Daily Cleaning",
        description: "Clean with multi-purpose solution using gentle rubbing motion for 30-45 seconds on each side of the lens.",
        icon: <Droplets size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 4,
        title: "Complete Rinse Cycle",
        description: "Rinse lens thoroughly with fresh solution for at least 10 seconds to remove all cleaning residue.",
        icon: <RefreshCw size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 5,
        title: "Premium Storage Care",
        description: "Store in clean case with fresh solution nightly. Replace case every 2-3 months and solution weekly.",
        icon: <Shield size={32} style={{ color: '#0d9488' }} />
      },
      {
        step: 6,
        title: "Annual Replacement",
        description: "Replace lenses exactly after 12 months of use. Mark replacement date on calendar for safety.",
        icon: <Calendar size={32} style={{ color: '#0d9488' }} />
      }
    ]
  };

  const safetyTips = [
    {
      icon: <AlertTriangle size={24} style={{ color: '#e74c3c' }} />,
      title: "Never Use Tap Water",
      description: "Only use sterile contact solution or saline for cleaning and storing lenses."
    },
    {
      icon: <Clock size={24} style={{ color: '#f39c12' }} />,
      title: "Don't Overwear",
      description: "Follow the recommended wearing schedule. Remove lenses if you experience discomfort."
    },
    {
      icon: <Eye size={24} style={{ color: '#3498db' }} />,
      title: "Check for Damage",
      description: "Inspect lenses before each use. Discard if torn, cracked, or damaged."
    },
    {
      icon: <Users size={24} style={{ color: '#9b59b6' }} />,
      title: "Don't Share Lenses",
      description: "Never share contact lenses with others, even if they have the same prescription."
    },
    {
      icon: <Droplets size={24} style={{ color: '#1abc9c' }} />,
      title: "Use Fresh Solution",
      description: "Always use fresh contact solution. Never top off old solution in your case."
    },
    {
      icon: <Timer size={24} style={{ color: '#e67e22' }} />,
      title: "Remove Before Sleep",
      description: "Always remove lenses before sleeping unless specifically designed for extended wear."
    }
  ];

  return (
    <div className="nova-website">
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
        color: 'white',
        padding: '1rem 0 2rem',
        marginTop: '4rem'
      }}>
        <div style={{ 
          maxWidth: '80rem', 
          margin: '0 auto', 
          padding: '0 1rem'
        }}>
          <Link 
            to="/" 
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: 'white',
              textDecoration: 'none',
              fontWeight: '500',
              marginBottom: '1rem',
              padding: '0.5rem 1rem',
              borderRadius: '0.5rem',
              transition: 'background-color 0.2s'
            }}
          >
            <ArrowLeft size={20} />
            Back to Home
          </Link>
          <h1 style={{ 
            fontSize: 'clamp(1.5rem, 4vw, 3rem)', 
            fontWeight: '700', 
            marginBottom: '0.5rem', 
            textAlign: 'center',
            lineHeight: '1.2'
          }}>
            Contact Lens Usage Instructions
          </h1>
          <p style={{ 
            fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', 
            textAlign: 'center', 
            opacity: 0.9,
            margin: '0 auto',
            maxWidth: '600px'
          }}>
            Step-by-step guide for safe and proper contact lens care
          </p>
        </div>
      </div>

      <div style={{ 
        maxWidth: '80rem', 
        margin: '0 auto', 
        padding: 'clamp(1rem, 4vw, 2rem) 1rem'
      }}>
        {/* Lens Type Tabs */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          marginBottom: '3rem',
          background: 'white',
          borderRadius: '1rem',
          padding: '0.5rem',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          overflowX: 'auto',
          gap: '0.25rem'
        }}>
          {['daily', 'weekly', 'monthly', 'yearly'].map(type => (
            <button
              key={type}
              onClick={() => setActiveTab(type)}
              style={{
                padding: 'clamp(0.75rem, 2vw, 1rem) clamp(1rem, 3vw, 2rem)',
                border: 'none',
                borderRadius: '0.75rem',
                background: activeTab === type ? 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' : 'transparent',
                color: activeTab === type ? 'white' : '#64748b',
                fontWeight: '600',
                fontSize: 'clamp(0.875rem, 2.5vw, 1rem)',
                cursor: 'pointer',
                transition: 'all 0.2s',
                textTransform: 'capitalize',
                whiteSpace: 'nowrap',
                flexShrink: 0,
                minWidth: 'fit-content'
              }}
            >
              {type} Lenses
            </button>
          ))}
        </div>

        {/* Instructions Grid */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(min(300px, 100%), 1fr))',
          gap: 'clamp(1rem, 3vw, 2rem)',
          marginBottom: '4rem'
        }}>
          {instructionSteps[activeTab].map((instruction, index) => (
            <div
              key={index}
              style={{
                background: 'white',
                borderRadius: '1rem',
                padding: 'clamp(1.25rem, 3vw, 2rem)',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                border: '2px solid #f1f5f9',
                transition: 'all 0.3s',
                width: '100%',
                maxWidth: '100%'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'clamp(0.75rem, 2vw, 1rem)',
                marginBottom: '1rem',
                flexWrap: 'wrap'
              }}>
                <div style={{
                  width: 'clamp(2.5rem, 6vw, 3rem)',
                  height: 'clamp(2.5rem, 6vw, 3rem)',
                  borderRadius: '50%',
                  background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: '700',
                  fontSize: 'clamp(1rem, 2.5vw, 1.25rem)',
                  flexShrink: 0
                }}>
                  {instruction.step}
                </div>
                {instruction.icon}
              </div>
              <h3 style={{
                fontSize: 'clamp(1.125rem, 3vw, 1.25rem)',
                fontWeight: '700',
                color: '#1e293b',
                marginBottom: '0.75rem',
                lineHeight: '1.3'
              }}>
                {instruction.title}
              </h3>
              <p style={{
                color: '#64748b',
                lineHeight: '1.6',
                margin: 0,
                fontSize: 'clamp(0.875rem, 2.25vw, 1rem)'
              }}>
                {instruction.description}
              </p>
            </div>
          ))}
        </div>

        {/* Safety Tips Section */}
        <div style={{
          background: 'white',
          borderRadius: '1rem',
          padding: 'clamp(1.5rem, 4vw, 2rem)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            fontSize: '2rem',
            fontWeight: '700',
            color: '#1e293b',
            textAlign: 'center',
            marginBottom: '2rem'
          }}>
            Important Safety Tips
          </h2>
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(280px, 100%), 1fr))',
            gap: 'clamp(1rem, 3vw, 1.5rem)'
          }}>
            {safetyTips.map((tip, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  gap: '1rem',
                  padding: '1.5rem',
                  background: '#f8fafc',
                  borderRadius: '0.75rem',
                  border: '1px solid #e2e8f0'
                }}
              >
                <div style={{ flexShrink: 0 }}>
                  {tip.icon}
                </div>
                <div>
                  <h4 style={{
                    fontSize: '1.125rem',
                    fontWeight: '600',
                    color: '#1e293b',
                    marginBottom: '0.5rem',
                    margin: '0 0 0.5rem 0'
                  }}>
                    {tip.title}
                  </h4>
                  <p style={{
                    color: '#64748b',
                    fontSize: '0.875rem',
                    lineHeight: '1.5',
                    margin: 0
                  }}>
                    {tip.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Emergency Contact */}
        <div style={{
          background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
          color: 'white',
          borderRadius: '1rem',
          padding: '2rem',
          marginTop: '3rem',
          textAlign: 'center'
        }}>
          <h3 style={{
            fontSize: '1.5rem',
            fontWeight: '700',
            marginBottom: '1rem'
          }}>
            Need Help or Experiencing Discomfort?
          </h3>
          <p style={{
            marginBottom: '1.5rem',
            opacity: 0.9
          }}>
            If you experience persistent discomfort, redness, or vision problems, remove your lenses immediately and consult your eye care professional.
          </p>
          <div style={{
            display: 'flex',
            gap: '1rem',
            justifyContent: 'center',
            flexWrap: 'wrap'
          }}>
            <Link 
              to="/"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.75rem 1.5rem',
                background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
                color: 'white',
                textDecoration: 'none',
                borderRadius: '0.5rem',
                fontWeight: '600',
                transition: 'all 0.2s'
              }}
            >
              <Eye size={18} />
              Contact Us
            </Link>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer style={{
        background: '#0f172a',
        color: 'white',
        padding: '3rem 0'
      }}>
        <div style={{
          maxWidth: '80rem',
          margin: '0 auto',
          padding: '0 1rem',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '2rem'
        }}>
          <div>
            <Logo />
            <p style={{ marginTop: '0.5rem', color: '#94a3b8' }}>
              Premium contact lenses for sensitive eyes
            </p>
          </div>
          <div style={{ textAlign: 'center' }}>
            <p style={{ color: '#94a3b8', fontSize: '0.875rem' }}>
              © 2025 Nova Lenses. All rights reserved.
            </p>
            <p style={{ color: '#64748b', fontSize: '0.75rem', marginTop: '0.25rem' }}>
              ISO 13485 Certified | CE Marked
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LensInstructions;